/**
 * Restoration Map Widget
 *
 * Interactive Mapbox GL map showing BOP water body outlines with
 * oyster restoration counts. Designed to sit behind Webflow text/overlays.
 *
 * Mode 1 (default): Static backdrop — no zoom/pan/rotate. Scroll moves the
 *   page, not the map. Water body labels with oyster counts are shown.
 *   Clicking a water body emits a "waterBodyClick" event.
 *
 * Mode 2 (explore): Full interactivity — zoom, pan, rotate, click.
 *   Triggered via BOPMap.setMode("explore").
 *
 * Data attributes:
 *   data-theme        — "light" | "dark" (default: dark)
 *   data-pitch        — camera pitch (default: 45)
 *   data-bearing      — camera bearing (default: 30)
 *   data-zoom         — zoom level (default: auto-fit to all 5 water bodies)
 *   data-center-lat   — center latitude (setting zoom/center disables auto-fit)
 *   data-center-lng   — center longitude
 *   data-interactive  — "true" enables pan/zoom on load (default: static backdrop)
 *   data-show-labels  — show labels on load (default: true)
 *   data-height       — CSS height (default: 100%)
 *   data-show-sites   — plot reef sites as dots (default: true)
 *   data-sites-url    — reef sites feed url (default: https://bop-nyc.web.app/api/reef-sites)
 *
 * The dots come live from the Webflow "Reef Sites" collection via the
 * /api/reef-sites Cloud Function, which returns only published sites with
 * "Show on Site" toggled ON. (The old static reef-sites.json is retired.)
 */

import React, { useEffect, useRef, useState } from "react";
import type { WidgetConfig } from "@bop/shared";
import {
  MAPBOX_CONFIG,
  WATER_BODY_LABELS,
  BOP_BRAND,
  DISPLAY_BOUNDS,
  DISPLAY_BOUNDS_PADDING as DESKTOP_PADDING,
} from "./constants";

// Tablet & down fit padding falls back to these, but every side is tunable per
// Webflow breakpoint via --map-fit-{top,bottom,left,right} on the map container,
// so the framed harbor clears whatever UI you overlay on mobile (e.g. a bottom
// ORS bar → set --map-fit-bottom: 45vh). Accepts px / vh / vw / rem / %.
const NARROW_FIT_FALLBACK = { top: 40, bottom: 40, left: 20, right: 20 };
import { WATER_BODIES } from "./water-bodies.geojson";
import { createMapEventBus } from "./map-events";
import type { FlyToTarget } from "./map-events";

// Mapbox GL JS is loaded from CDN — declared as global
declare const mapboxgl: {
  accessToken: string;
  Map: new (options: Record<string, unknown>) => MapInstance;
  Marker: new (options: Record<string, unknown>) => MarkerInstance;
};

// The widget can mount before Mapbox GL exists: the loader's ensureMapbox gate
// only covers the lazy-load path, and this bundle is also loaded directly via a
// site-wide <script type="module"> tag, which registers + mounts immediately.
// Self-load the engine here so the page needs no blocking Mapbox tag at all.
const MAPBOX_JS = "https://api.mapbox.com/mapbox-gl-js/v3.9.4/mapbox-gl.js";
const MAPBOX_CSS = "https://api.mapbox.com/mapbox-gl-js/v3.9.4/mapbox-gl.css";
let mapboxGlPromise: Promise<void> | null = null;

function ensureMapboxGl(): Promise<void> {
  if (typeof mapboxgl !== "undefined") return Promise.resolve();
  if (mapboxGlPromise) return mapboxGlPromise;

  mapboxGlPromise = new Promise<void>((resolve, reject) => {
    if (!document.querySelector(`link[href="${MAPBOX_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = MAPBOX_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${MAPBOX_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("mapbox-gl failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = MAPBOX_JS;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      mapboxGlPromise = null;
      reject(new Error("mapbox-gl failed"));
    };
    document.head.appendChild(script);
  });

  return mapboxGlPromise;
}

/* On touch devices Mapbox sets touch-action:none on its canvas, so a finger on
   the (static-backdrop) map can't scroll the page — the map becomes a scroll
   trap. Allow pan gestures through to the browser; map exploration on touch is
   two-finger via cooperativeGestures, which uses the same pan-x pan-y model. */
const MAP_TOUCH_CSS = `
@media (pointer: coarse){
.bop-widget--restoration-map .mapboxgl-canvas-container,
.bop-widget--restoration-map .mapboxgl-canvas{touch-action:pan-x pan-y !important}
}`;
let mapTouchCssInjected = false;
function injectMapTouchCss() {
  if (mapTouchCssInjected) return;
  mapTouchCssInjected = true;
  const st = document.createElement("style");
  st.textContent = MAP_TOUCH_CSS;
  document.head.appendChild(st);
}

interface MapInstance {
  on: (event: string, layerOrCb: string | ((e?: unknown) => void), cb?: (e?: unknown) => void) => void;
  addSource: (id: string, source: Record<string, unknown>) => void;
  getSource: (id: string) => { setData: (data: unknown) => void } | undefined;
  addLayer: (layer: Record<string, unknown>) => void;
  setPaintProperty: (layer: string, prop: string, value: unknown) => void;
  flyTo: (options: Record<string, unknown>) => void;
  fitBounds: (bounds: [[number, number], [number, number]], options?: Record<string, unknown>) => void;
  resize: () => void;
  getCanvas: () => HTMLElement;
  remove: () => void;
  scrollZoom: { enable: () => void; disable: () => void };
  boxZoom: { enable: () => void; disable: () => void };
  dragRotate: { enable: () => void; disable: () => void };
  dragPan: { enable: () => void; disable: () => void };
  keyboard: { enable: () => void; disable: () => void };
  doubleClickZoom: { enable: () => void; disable: () => void };
  touchZoomRotate: { enable: () => void; disable: () => void };
  touchPitch: { enable: () => void; disable: () => void };
}

interface MarkerInstance {
  setLngLat: (lngLat: [number, number]) => MarkerInstance;
  addTo: (map: MapInstance) => MarkerInstance;
  remove: () => void;
}

interface RestorationMapProps {
  config: WidgetConfig;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

/**
 * Read live "Introduced Total" per region from the CMS-bound water-body cards
 * (.wb-card[data-region-key][data-introduced-total]) rendered by Webflow on the
 * page. Keyed by region-key so the map labels stay in lockstep with the
 * dashboard list and the Water Bodies collection — no duplicated hardcoded
 * numbers. Returns {} when the cards aren't present (map used standalone).
 */
function readCmsIntroducedTotals(): Record<string, number> {
  const totals: Record<string, number> = {};
  document
    .querySelectorAll<HTMLElement>("[data-region-key][data-introduced-total]")
    .forEach((el) => {
      const key = el.dataset.regionKey?.trim();
      // CMS number fields render with grouping/whitespace; strip non-digits.
      const raw = (el.dataset.introducedTotal ?? "").replace(/[^0-9.-]/g, "");
      const val = parseFloat(raw);
      if (key && Number.isFinite(val)) totals[key] = val;
    });
  return totals;
}

const DEFAULT_SITES_URL = "https://bop-nyc.web.app/api/reef-sites";

// Water body paint — soft/ghostly at rest; the hovered body gets a glow and a
// stronger border (per-feature opacity expressions keyed on the hovered id).
const PAINT = {
  fill: { base: 0.05, hover: 0.09 },
  line: { base: 0.15, hover: 0.5 },
  glow: { base: 0.06, hover: 0.35 },
};
const FILL_OPACITY_FLASH = 0.3;

/** opacity expression: `hover` for the hovered water body, `base` for the rest */
function hoverCase(hoverId: string | null, vals: { base: number; hover: number }): unknown {
  return [
    "case",
    ["==", ["get", "id"], hoverId ?? "__none__"],
    vals.hover,
    vals.base,
  ];
}

/**
 * Padding for framing a focused region: the expanded menu overlays the left
 * half on desktop, so the region is framed in the right 50% (with ~2rem
 * gutters). Mobile keeps the regular fit padding.
 */
function isTabletDown() {
  return typeof window !== "undefined" && window.innerWidth <= 991;
}

// Resolve one --map-fit-* value (px / vh / vw / rem / %) to pixels; `axisPx` is
// the container dimension a % is measured against. Falls back when unset/invalid.
function readFitVar(cs: CSSStyleDeclaration | null, name: string, axisPx: number, def: number) {
  const raw = (cs?.getPropertyValue(name) ?? "").trim();
  if (!raw) return def;
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return def;
  if (raw.endsWith("vh")) return (n / 100) * window.innerHeight;
  if (raw.endsWith("vw")) return (n / 100) * window.innerWidth;
  if (raw.endsWith("rem")) return n * parseFloat(getComputedStyle(document.documentElement).fontSize || "16");
  if (raw.endsWith("%")) return (n / 100) * axisPx;
  return n; // px / unitless
}

// Fit padding tunable via --map-fit-{top,bottom,left,right} on the map container.
function cssFitPadding(container: HTMLElement | null, fallback: { top: number; bottom: number; left: number; right: number }) {
  const cs = container ? getComputedStyle(container) : null;
  const h = container?.clientHeight || window.innerHeight;
  const w = container?.clientWidth || window.innerWidth;
  return {
    top: readFitVar(cs, "--map-fit-top", h, fallback.top),
    bottom: readFitVar(cs, "--map-fit-bottom", h, fallback.bottom),
    left: readFitVar(cs, "--map-fit-left", w, fallback.left),
    right: readFitVar(cs, "--map-fit-right", w, fallback.right),
  };
}

// Overview framing (whole harbor). Tablet & down → CSS-var-tunable padding so the
// bounds clear the mobile overlay; desktop keeps its symmetric gutters.
function overviewPadding(container: HTMLElement | null) {
  return isTabletDown() ? cssFitPadding(container, NARROW_FIT_FALLBACK) : DESKTOP_PADDING;
}

function focusPadding(container: HTMLElement | null) {
  if (isTabletDown()) return cssFitPadding(container, NARROW_FIT_FALLBACK);
  const w = container?.clientWidth ?? 0;
  return { top: 48, bottom: 48, right: 32, left: Math.round(w / 2) + 32 };
}

/** Per-region bounding boxes computed from the real polygon geometry. */
const REGION_BOUNDS: Record<string, [[number, number], [number, number]]> = {};
for (const feature of WATER_BODIES.features) {
  const id = feature.properties?.id as string | undefined;
  const geom = feature.geometry;
  if (!id || (geom.type !== "Polygon" && geom.type !== "MultiPolygon")) continue;
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  const polys = geom.type === "MultiPolygon" ? geom.coordinates : [geom.coordinates];
  for (const poly of polys) {
    for (const [lng, lat] of poly[0]) {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    }
  }
  REGION_BOUNDS[id] = [[minLng, minLat], [maxLng, maxLat]];
}

/**
 * Loose NY-metro sanity bounds. The feed is already curated server-side via
 * "Show on Site", so this only guards against null-island / fat-fingered
 * coordinates flinging the map — it must NOT clip legit curated sites up on the
 * Sound (Rye) or the Hudson (Tappan Zee). Keep it generous.
 */
const SITES_BBOX = { minLng: -74.7, maxLng: -73.3, minLat: 40.3, maxLat: 41.2 };

interface ReefSiteEntry {
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  status?: string | null;
  siteType?: string | null;
}

// The feed serves both Site Types gated by "Show on Map", so this map has to
// narrow to its own type or the ORS Stations (the ORS finder's dots) land here
// too. Missing siteType counts as a reef site, matching the ORS finder's own
// default, so an item with the field unset shows here rather than nowhere.
const isReefSite = (s: ReefSiteEntry) => (s.siteType ?? "Reef Site") === "Reef Site";

// The feed is already gated by "Show on Site" server-side, so no status filter
// here — just a coordinate sanity check (finite + inside the harbor bbox) to
// drop any dot with missing/garbage lat-lng that would fling the map.
function sitesToGeoJSON(sites: ReefSiteEntry[]) {
  return {
    type: "FeatureCollection" as const,
    features: sites
      .filter(
        (s) =>
          !isNaN(s.latitude) &&
          !isNaN(s.longitude) &&
          s.latitude >= SITES_BBOX.minLat &&
          s.latitude <= SITES_BBOX.maxLat &&
          s.longitude >= SITES_BBOX.minLng &&
          s.longitude <= SITES_BBOX.maxLng
      )
      .map((s) => ({
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: [s.longitude, s.latitude] },
        properties: { slug: s.slug, name: s.name },
      })),
  };
}

export function RestorationMap({ config }: RestorationMapProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapInstance | null>(null);
  const markersRef = useRef<MarkerInstance[]>([]);
  const labelInnersRef = useRef<Record<string, HTMLElement>>({});

  const height = (config.height as string) ?? "100%";
  const pitch = config.pitch ? Number(config.pitch) : MAPBOX_CONFIG.defaultPitch;
  const bearing = config.bearing ? Number(config.bearing) : MAPBOX_CONFIG.defaultBearing;
  const zoom = config.zoom ? Number(config.zoom) : MAPBOX_CONFIG.defaultZoom;
  const centerLng = config.centerLng ? Number(config.centerLng) : MAPBOX_CONFIG.defaultCenter[0];
  const centerLat = config.centerLat ? Number(config.centerLat) : MAPBOX_CONFIG.defaultCenter[1];
  const showSites = config.showSites !== "false";
  const sitesUrl = (config.sitesUrl as string) ?? DEFAULT_SITES_URL;
  // Explicit zoom/center attrs opt out of the auto-fit camera
  const hasFixedCamera = Boolean(config.zoom || config.centerLat || config.centerLng);
  const startInteractive = config.interactive === "true";

  const [mapboxReady, setMapboxReady] = useState<boolean>(() => typeof mapboxgl !== "undefined");

  useEffect(() => {
    injectMapTouchCss();
  }, []);

  useEffect(() => {
    if (mapboxReady) return;
    let cancelled = false;
    ensureMapboxGl().then(
      () => {
        if (!cancelled) setMapboxReady(true);
      },
      () => console.error("[BOP] Mapbox GL JS failed to load.")
    );
    return () => {
      cancelled = true;
    };
  }, [mapboxReady]);

  useEffect(() => {
    if (!mapboxReady) return;
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = MAPBOX_CONFIG.publicToken;

    const map = new mapboxgl.Map({
      cooperativeGestures: window.matchMedia("(pointer: coarse)").matches,
      container: mapContainerRef.current,
      style: MAPBOX_CONFIG.styleUrl,
      // Default camera fits all 5 water bodies whatever the container shape;
      // explicit data-zoom / data-center-* attrs fall back to a fixed camera.
      ...(hasFixedCamera
        ? { center: [centerLng, centerLat], zoom }
        : { bounds: DISPLAY_BOUNDS, fitBoundsOptions: { padding: overviewPadding(mapContainerRef.current) } }),
      pitch,
      bearing,
      // Interactive so click/hover events fire on the water bodies, but we
      // immediately disable pan/zoom/rotate below to keep it a static backdrop.
      interactive: true,
      attributionControl: false,
      logoPosition: "bottom-right",
    });

    mapRef.current = map;
    let currentMode: "static" | "explore" = "static";
    let hoveredId: string | null = null;
    let waterRevealed = false;
    // When set, the camera is focused on one region (accordion selection) and
    // resize refits keep that region framed instead of the full harbor.
    let focusedId: string | null = null;

    // Static backdrop: clickable + hoverable, but no panning/zooming/rotating
    map.scrollZoom.disable();
    map.boxZoom.disable();
    map.dragRotate.disable();
    map.dragPan.disable();
    map.keyboard.disable();
    map.doubleClickZoom.disable();
    map.touchZoomRotate.disable();
    map.touchPitch.disable();

    // Keep the current frame (full harbor, or the focused region) in view when
    // the container changes size/aspect — e.g. the left menu expanding.
    let refitTimer: ReturnType<typeof setTimeout> | undefined;
    const refitObserver = new ResizeObserver(() => {
      if (hasFixedCamera || currentMode !== "static") return;
      clearTimeout(refitTimer);
      refitTimer = setTimeout(() => {
        map.resize();
        const bounds = (focusedId && REGION_BOUNDS[focusedId]) || DISPLAY_BOUNDS;
        const padding = focusedId
          ? focusPadding(mapContainerRef.current)
          : overviewPadding(mapContainerRef.current);
        map.fitBounds(bounds, { padding, duration: 300 });
      }, 150);
    });
    refitObserver.observe(mapContainerRef.current);
    // ─── Event Bus ──────────────────────────────────────
    const eventBus = createMapEventBus();

    // ─── Hover state (map layers + labels + external listeners) ───
    function styleLabelHover(id: string | null) {
      for (const [wbId, inner] of Object.entries(labelInnersRef.current)) {
        const on = wbId === id;
        inner.style.transform = on ? "scale(1.06)" : "scale(1)";
        inner.style.borderColor = on
          ? "rgba(255, 71, 0, 0.55)"
          : "rgba(255, 255, 255, 0.12)";
      }
    }

    function setHover(id: string | null) {
      if (id === hoveredId) return;
      hoveredId = id;
      if (waterRevealed) {
        map.setPaintProperty("water-bodies-fill", "fill-opacity", hoverCase(id, PAINT.fill));
        map.setPaintProperty("water-bodies-line", "line-opacity", hoverCase(id, PAINT.line));
        map.setPaintProperty("water-bodies-glow", "line-opacity", hoverCase(id, PAINT.glow));
      }
      styleLabelHover(id);
      eventBus.emit("waterBodyHover", id);
      document.dispatchEvent(
        new CustomEvent("bop-map:waterBodyHovered", {
          detail: id
            ? { waterBodyId: id, ...(WATER_BODY_LABELS[id] ?? {}) }
            : { waterBodyId: null },
        })
      );
    }

    map.on("style.load", () => {
      // Water body polygons from the local GeoJSON (the Studio tileset 404s).
      // Shapes are generated from NYSDEC/NHD data — see ./water-bodies.geojson.ts.
      map.addSource("water-bodies", {
        type: "geojson",
        data: WATER_BODIES,
      });

      // Outer glow layer (pops on hover)
      map.addLayer({
        id: "water-bodies-glow",
        type: "line",
        source: "water-bodies",
        paint: {
          "line-color": "#ffffff",
          "line-width": 10,
          "line-blur": 6,
          "line-opacity": 0,
        },
      });

      map.addLayer({
        id: "water-bodies-fill",
        type: "fill",
        source: "water-bodies",
        paint: {
          "fill-color": "#ffffff",
          "fill-opacity": 0,
        },
      });

      map.addLayer({
        id: "water-bodies-line",
        type: "line",
        source: "water-bodies",
        paint: {
          "line-color": "#ffffff",
          "line-width": 1,
          "line-opacity": 0,
        },
      });

      // Create label markers (after setHover exists so labels can drive hover)
      createLabelMarkers(map, eventBus, setHover);

      // Active reef site dots, layered above the water body fills
      if (showSites) {
        map.addSource("reef-sites", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });

        // Soft glow under each dot
        map.addLayer({
          id: "reef-sites-glow",
          type: "circle",
          source: "reef-sites",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 6, 13, 16],
            "circle-color": BOP_BRAND.colors.secondary2,
            "circle-blur": 1,
            "circle-opacity": 0,
          },
        });

        map.addLayer({
          id: "reef-sites-dots",
          type: "circle",
          source: "reef-sites",
          paint: {
            "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 2.5, 13, 6],
            "circle-color": BOP_BRAND.colors.secondary2,
            "circle-stroke-color": "rgba(255,255,255,0.85)",
            "circle-stroke-width": 1,
            "circle-opacity": 0,
            "circle-stroke-opacity": 0,
          },
        });

        fetch(sitesUrl)
          .then((r) => r.json())
          .then((sites: ReefSiteEntry[]) => {
            map.getSource("reef-sites")?.setData(sitesToGeoJSON(sites.filter(isReefSite)));
          })
          .catch((err) => console.error("[BOP] Failed to load reef sites:", err));
      }

      // Click handling on water body fills (active in both modes)
      map.on("click", "water-bodies-fill", (e: unknown) => {
        const event = e as { features?: Array<{ properties?: Record<string, unknown> }> };
        const feature = event.features?.[0];
        if (feature?.properties) {
          const waterBodyId = feature.properties.id as string;
          eventBus.emit("waterBodyClick", waterBodyId);

          document.dispatchEvent(
            new CustomEvent("bop-map:waterBodyClicked", {
              detail: {
                waterBodyId,
                name: feature.properties.name,
                ...(WATER_BODY_LABELS[waterBodyId] ?? {}),
              },
            })
          );
        }
      });

      map.on("mouseenter", "water-bodies-fill", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mousemove", "water-bodies-fill", (e: unknown) => {
        const event = e as { features?: Array<{ properties?: Record<string, unknown> }> };
        const id = event.features?.[0]?.properties?.id as string | undefined;
        if (id) setHover(id);
      });
      map.on("mouseleave", "water-bodies-fill", () => {
        map.getCanvas().style.cursor = "";
        setHover(null);
      });

      // Auto-reveal after load
      setTimeout(() => {
        revealLayer("water-bodies");
        revealLayer("labels");
        if (showSites) revealLayer("sites");
      }, 500);

      setMapLoaded(true);
      eventBus.emit("ready");
    });

    // ─── Mode switching ─────────────────────────────────
    function setMode(mode: "static" | "explore") {
      currentMode = mode;
      if (mode === "static" && !hasFixedCamera) {
        map.fitBounds(DISPLAY_BOUNDS, { padding: overviewPadding(mapContainerRef.current), duration: 1200 });
      }
      if (mode === "explore") {
        map.scrollZoom.enable();
        map.boxZoom.enable();
        map.dragRotate.enable();
        map.dragPan.enable();
        map.keyboard.enable();
        map.doubleClickZoom.enable();
        map.touchZoomRotate.enable();
        map.touchPitch.enable();
      } else {
        map.scrollZoom.disable();
        map.boxZoom.disable();
        map.dragRotate.disable();
        map.dragPan.disable();
        map.keyboard.disable();
        map.doubleClickZoom.disable();
        map.touchZoomRotate.disable();
        map.touchPitch.disable();
      }
    }

    // ─── API Handlers ───────────────────────────────────
    function flyToTarget(target: FlyToTarget, options?: { duration?: number }) {
      if (typeof target === "string") {
        // Frame the region's real geometry (falls back to the label point)
        const bounds = REGION_BOUNDS[target];
        if (bounds) {
          focusedId = target;
          map.fitBounds(bounds, {
            padding: focusPadding(mapContainerRef.current),
            pitch: MAPBOX_CONFIG.defaultPitch,
            bearing: MAPBOX_CONFIG.defaultBearing,
            duration: options?.duration ?? 1600,
          });
          return;
        }
        const info = WATER_BODY_LABELS[target];
        if (!info) return;
        map.flyTo({
          center: info.position,
          zoom: 12,
          pitch: MAPBOX_CONFIG.defaultPitch,
          bearing: MAPBOX_CONFIG.defaultBearing,
          duration: options?.duration ?? 2000,
        });
      } else {
        map.flyTo({
          center: target.center,
          zoom: target.zoom ?? zoom,
          pitch: target.pitch ?? pitch,
          bearing: target.bearing ?? bearing,
          duration: target.duration ?? options?.duration ?? 2000,
        });
      }
    }

    function highlightWaterBody(waterBodyId: string) {
      map.setPaintProperty("water-bodies-fill", "fill-opacity", [
        "case",
        ["==", ["get", "id"], waterBodyId], FILL_OPACITY_FLASH,
        PAINT.fill.base,
      ]);
      setTimeout(() => {
        map.setPaintProperty("water-bodies-fill", "fill-opacity", hoverCase(hoveredId, PAINT.fill));
      }, 1500);
    }

    function revealLayer(target: string) {
      if (target === "water-bodies") {
        waterRevealed = true;
        map.setPaintProperty("water-bodies-fill", "fill-opacity", hoverCase(hoveredId, PAINT.fill));
        map.setPaintProperty("water-bodies-line", "line-opacity", hoverCase(hoveredId, PAINT.line));
        map.setPaintProperty("water-bodies-glow", "line-opacity", hoverCase(hoveredId, PAINT.glow));
      } else if (target === "labels") {
        document.querySelectorAll<HTMLElement>(".bop-water-label").forEach((el) => {
          el.style.opacity = "1";
        });
      } else if (target === "sites") {
        map.setPaintProperty("reef-sites-dots", "circle-opacity", 1);
        map.setPaintProperty("reef-sites-dots", "circle-stroke-opacity", 1);
        map.setPaintProperty("reef-sites-glow", "circle-opacity", 0.35);
      } else if (WATER_BODY_LABELS[target]) {
        highlightWaterBody(target);
        const labelEl = document.querySelector<HTMLElement>(
          `.bop-water-label[data-water-body-id="${target}"]`
        );
        if (labelEl) labelEl.style.opacity = "1";
      }
    }

    function toggleLabels(visible: boolean) {
      document.querySelectorAll<HTMLElement>(".bop-water-label").forEach((el) => {
        el.style.opacity = visible ? "1" : "0";
      });
    }

    function resetView() {
      focusedId = null;
      if (hasFixedCamera) {
        map.flyTo({
          center: [centerLng, centerLat],
          zoom, pitch, bearing,
          duration: 2000,
        });
      } else {
        map.fitBounds(DISPLAY_BOUNDS, {
          padding: overviewPadding(mapContainerRef.current),
          pitch,
          bearing,
          duration: 2000,
        });
      }
    }

    function updateOysterCount(waterBodyId: string, count: number) {
      const labelEl = document.querySelector<HTMLElement>(
        `.bop-water-label[data-water-body-id="${waterBodyId}"] .bop-water-label__count`
      );
      if (labelEl) animateCounter(labelEl, count);
    }

    eventBus.registerHandler({
      flyTo: flyToTarget,
      highlight: highlightWaterBody,
      showLabels: toggleLabels,
      reset: resetView,
      setOysterCount: updateOysterCount,
      reveal: revealLayer,
      setMode,
      hover: setHover,
    });

    if (startInteractive) setMode("explore");

    return () => {
      refitObserver.disconnect();
      clearTimeout(refitTimer);
      markersRef.current.forEach((m) => m.remove());
      map.remove();
      mapRef.current = null;
    };
  }, [mapboxReady]);

  const [, setMapLoaded] = useState(false);

  function createLabelMarkers(
    map: MapInstance,
    eventBus: { emit: (event: string, data?: unknown) => void },
    setHover: (id: string | null) => void
  ) {
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    labelInnersRef.current = {};

    // Single source of truth: the CMS-bound water-body cards
    // (.wb-card[data-region-key][data-introduced-total]) carry the live
    // Introduced Total. Read them so the map labels match the dashboard list
    // exactly. Falls back to the constants when the cards aren't on the page.
    const cmsTotals = readCmsIntroducedTotals();

    for (const [id, info] of Object.entries(WATER_BODY_LABELS)) {
      const restored = cmsTotals[id] ?? info.oystersRestored;
      const el = document.createElement("div");
      el.className = "bop-water-label";
      el.dataset.waterBodyId = id;
      // Mapbox owns el.style.transform (positioning) — NEVER overwrite it or the
      // marker jumps to the top-left. Scale the inner wrapper on hover instead.
      el.style.cssText = `
        pointer-events: auto;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.8s ease;
      `;

      // Translucent blurred chip so the text stays readable over the basemap
      const inner = document.createElement("div");
      inner.className = "bop-water-label__inner";
      inner.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 9px 14px 8px;
        border-radius: 10px;
        background: rgba(8, 12, 18, 0.55);
        border: 1px solid rgba(255, 255, 255, 0.12);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
        transition: transform 0.3s ease, border-color 0.3s ease;
        text-shadow: 0 1px 6px rgba(0,0,0,0.8);
      `;
      labelInnersRef.current[id] = inner;

      // Click on label → same as clicking the water body
      el.addEventListener("click", () => {
        eventBus.emit("waterBodyClick", id);
        document.dispatchEvent(
          new CustomEvent("bop-map:waterBodyClicked", {
            detail: {
              waterBodyId: id,
              waterBodyName: info.name,
              shortName: info.shortName,
              oystersRestored: restored,
            },
          })
        );
      });

      // Hovering the label drives the shared hover state (map glow + chip lift)
      el.addEventListener("mouseenter", () => setHover(id));
      el.addEventListener("mouseleave", () => setHover(null));

      // Oyster count
      const countEl = document.createElement("div");
      countEl.className = "bop-water-label__count";
      countEl.dataset.countTarget = String(restored);
      countEl.style.cssText = `
        font-family: ${BOP_BRAND.fonts.heading};
        font-size: 1.6rem;
        font-weight: 700;
        color: ${BOP_BRAND.colors.secondary2};
        text-transform: uppercase;
        letter-spacing: -0.02em;
        line-height: 1;
      `;
      countEl.textContent = formatNumber(restored);

      // "oysters restored" subtitle
      const subtitleEl = document.createElement("div");
      subtitleEl.style.cssText = `
        font-family: ${BOP_BRAND.fonts.body};
        font-size: 0.55rem;
        font-weight: 400;
        color: ${BOP_BRAND.colors.primary5};
        letter-spacing: 0.08em;
        text-transform: uppercase;
        margin-top: 1px;
        opacity: 0.85;
      `;
      subtitleEl.textContent = "oysters restored";

      // Water body name
      const nameEl = document.createElement("div");
      nameEl.className = "bop-water-label__name";
      nameEl.style.cssText = `
        font-family: ${BOP_BRAND.fonts.body};
        font-size: 0.65rem;
        font-weight: 600;
        color: ${BOP_BRAND.colors.white};
        letter-spacing: 0.04em;
        text-transform: uppercase;
        margin-top: 6px;
        padding-top: 4px;
        border-top: 1px solid rgba(255,255,255,0.2);
      `;
      nameEl.textContent = info.shortName;

      inner.appendChild(countEl);
      inner.appendChild(subtitleEl);
      inner.appendChild(nameEl);
      el.appendChild(inner);

      const marker = new mapboxgl.Marker({ element: el, anchor: "center" })
        .setLngLat(info.position)
        .addTo(map);

      markersRef.current.push(marker);
    }
  }

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height,
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    />
  );
}

function animateCounter(el: HTMLElement, target: number, duration = 1500) {
  const current = parseInt(el.dataset.countTarget ?? "0", 10);
  const start = performance.now();
  const diff = target - current;

  function tick(now: number) {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - (1 - progress) * (1 - progress);
    const value = Math.round(current + diff * eased);
    el.textContent = formatNumber(value);
    if (progress < 1) requestAnimationFrame(tick);
    else el.dataset.countTarget = String(target);
  }

  requestAnimationFrame(tick);
}

// Self-register
interface BopWidgets {
  registerWidget: (type: string, component: React.ComponentType<{ config: WidgetConfig }>) => void;
}
const bop = (window as unknown as Record<string, unknown>).__BOP_WIDGETS__ as BopWidgets | undefined;
if (bop) bop.registerWidget("restoration-map", RestorationMap);

export default RestorationMap;
